import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { FeatureFlagsProvider } from '@/context/FeatureFlagsProvider'
import { AppLayout } from '@/components/layout/AppLayout'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'

const ThreadsPage = lazy(() => import('./pages/ThreadsPage').then(m => ({ default: m.ThreadsPage })))
const ThreadDetailPage = lazy(() => import('./pages/ThreadDetailPage').then(m => ({ default: m.ThreadDetailPage })))
const PersonasPage = lazy(() => import('./pages/PersonasPage').then(m => ({ default: m.PersonasPage })))
const PersonaDetailPage = lazy(() => import('./pages/PersonaDetailPage').then(m => ({ default: m.PersonaDetailPage })))
const SearchPage = lazy(() => import('./pages/SearchPage').then(m => ({ default: m.SearchPage })))
const KnowledgePage = lazy(() => import('./pages/KnowledgePage').then(m => ({ default: m.KnowledgePage })))
const KnowledgeArticleDetailPage = lazy(() => import('./pages/KnowledgeArticleDetailPage').then(m => ({ default: m.KnowledgeArticleDetailPage })))
const KnowledgeQueryPage = lazy(() => import('./pages/KnowledgeQueryPage').then(m => ({ default: m.KnowledgeQueryPage })))
const GovernorPage = lazy(() => import('./pages/GovernorPage').then(m => ({ default: m.GovernorPage })))
const EvalRunsPage = lazy(() => import('./pages/EvalRunsPage').then(m => ({ default: m.EvalRunsPage })))
const NewEvalRunPage = lazy(() => import('./pages/NewEvalRunPage').then(m => ({ default: m.NewEvalRunPage })))
const EvalRunDetailPage = lazy(() => import('./pages/EvalRunDetailPage').then(m => ({ default: m.EvalRunDetailPage })))
const EvalSuggestionDetailPage = lazy(() => import('./pages/EvalSuggestionDetailPage').then(m => ({ default: m.EvalSuggestionDetailPage })))
const PromptLabPage = lazy(() => import('./pages/PromptLabPage').then(m => ({ default: m.PromptLabPage })))
const NewPromptExperimentPage = lazy(() => import('./pages/NewPromptExperimentPage').then(m => ({ default: m.NewPromptExperimentPage })))
const PromptExperimentDetailPage = lazy(() => import('./pages/PromptExperimentDetailPage').then(m => ({ default: m.PromptExperimentDetailPage })))

function LoginPage() {
  const { login } = useAuth()

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100">
      <Card className="w-full max-w-md p-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-2 text-gray-600">Sign in with your Microsoft account to continue.</p>
        </div>

        <Button className="mt-6 w-full" autoFocus={true} onClick={login}>
          Sign in with Microsoft
        </Button>
      </Card>
    </div>
  )
}

function LoadingPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  )
}

export function App() {
  const { isAuthenticated} = useAuth()

  if (!isAuthenticated) {
    return <LoginPage />
  }

  return (
    <FeatureFlagsProvider>
      <AppLayout>
        <Suspense fallback={<LoadingPage />}>
          <Routes>
            <Route path="/" element={<ThreadsPage />} />
            <Route path="/threads/:threadId" element={<ThreadDetailPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/personas" element={<PersonasPage />} />
            <Route path="/personas/:personaId" element={<PersonaDetailPage />} />
            <Route path="/knowledge" element={<KnowledgePage />} />
            <Route path="/knowledge-query" element={<KnowledgeQueryPage />} />
            <Route path="/knowledge/:articleId" element={<KnowledgeArticleDetailPage />} />
            <Route path="/governor" element={<GovernorPage />} />
            <Route path="/eval-runs" element={<EvalRunsPage />} />
            <Route path="/eval-runs/new" element={<NewEvalRunPage />} />
            <Route path="/eval-runs/:runId" element={<EvalRunDetailPage />} />
            <Route path="/eval-suggestions/:suggestionId" element={<EvalSuggestionDetailPage />} />
            <Route path="/prompt-lab" element={<PromptLabPage />} />
            <Route path="/prompt-lab/new" element={<NewPromptExperimentPage />} />
            <Route path="/prompt-lab/:experimentId" element={<PromptExperimentDetailPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AppLayout>
    </FeatureFlagsProvider>
  )
}
